/**
 * Encapsulates save, hold-save, and discard actions for the CPT codes page.
 *
 * Handles the "Save Changes" flow (auth-status comments insert, denial/query
 * reason flush, CPT API update, context cleanup), the "Save Hold" flow
 * (Firestore timestamp update), and the "Proceed Without Saving" discard flow.
 *
 * @module useCptSaveActions
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useEffect, useState } from "react";
import { clearDenialQueryReasonsFromStoreByOrderId } from "../../../../../components/modals/StatusUpdateModal/utils/denialQueryReasonUserStoreHelper";
import { flushDenialQueryReasonsToApi } from "../../../../../components/modals/StatusUpdateModal/utils/flushDenialQueryReasonsToApi";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { useBusniessOfficeNotesContext } from "../../../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { User } from "../../../../../data-model/firebaseAuthModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { SingleOrderDetailsTableName } from "../../../../../enums/singleOrderDetailsTableName";
import { useAuthStatusCommentsInsert } from "../../../../../hooks/singleMedicalPaOrderDetails/useAuthStatusCommentsInsert";
import { useUpdateOrderDetails } from "../../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { showApiError } from "../../../../../utils/showApiError";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";
import { insertAuthStatusCommentsFromStore } from "../utils/insertAuthStatusCommentsFromStore";
import { transformTableDataToApiPayload } from "../utils/transformRowDataForPayload";
import { updateCptTablesForOrderIds } from "../utils/updateCptTablesForOrderIds";

/**
 * Dependencies and setters wired into {@link useCptSaveActions}.
 */
interface UseCptSaveActionsParams {
  /** Current order details used for analytics, inserts, and regimen name. */
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  /** Auth-on-file details for the active order (unused by save handlers today). */
  authOnFileDetails: AuthOnFile | null | undefined;
  /** Active medical PA order ID. */
  orderId: string | undefined;
  /** Authenticated user used for analytics event attribution. */
  user: User | null | undefined;
  /** Payer name attached to analytics payloads. */
  payerName: string;
  /** Auth-status option catalog used when transforming CPT table rows. */
  authStatusOptions: AuthStatusOptionModel[];
  /** Current editable CPT table rows. */
  tableData: CptCodesTableRowModel[];
  /** Last-saved CPT table snapshot used for discard/revert. */
  originalTableData: CptCodesTableRowModel[];
  /** Whether the table has unsaved edits. */
  hasChanges: boolean;
  /** Replaces the editable CPT table rows. */
  setTableData: React.Dispatch<React.SetStateAction<CptCodesTableRowModel[]>>;
  /** Replaces the last-saved CPT table snapshot. */
  setOriginalTableData: React.Dispatch<
    React.SetStateAction<CptCodesTableRowModel[]>
  >;
  /** Updates the unsaved-changes flag. */
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  /** Persists table rows into the single-order data context / UserStore. */
  saveTableDataToContext: (data: CptCodesTableRowModel[]) => void;
  /** Clears staged BO comments for an order. */
  removeBoComments: (orderId: string) => void;
  /** Clears cached CPT codes for an order. */
  removeCptCodes: (orderId: string) => void;
  /** Clears staged auth-status change comments for an order. */
  removeAuthStatusChangeComments: (orderId: string) => void;
  /** Redux dispatch used to close modals after save. */
  dispatch: AppDispatch;
  /** Optional navigation-blocker proceed callback after save or discard. */
  blockerProceed: (() => void) | undefined;
}

/**
 * Provides save, hold-save, and discard handlers for the CPT codes table.
 *
 * @param params - Table state, order data, and cleanup callbacks
 * @returns `isSaving`, `isHoldSaving`, `handleSaveChanges`, `handleSaveHold`,
 *          `handleProceedWithoutSaving`
 */
export const useCptSaveActions = ({
  // singleMedicalPaOrder,
  generalOrderDetails,
  authOnFileDetails,
  orderId,
  user,
  payerName,
  authStatusOptions,
  tableData,
  originalTableData,
  hasChanges,
  setTableData,
  setOriginalTableData,
  setHasChanges,
  saveTableDataToContext,
  removeBoComments,
  removeCptCodes,
  removeAuthStatusChangeComments,
  dispatch,
  blockerProceed,
}: UseCptSaveActionsParams) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isHoldSaving, setIsHoldSaving] = useState(false);
  const { runUpdate, abort } = useUpdateOrderDetails({
    orderId: orderId ?? "",
    orgId: getOrgIdForFetchExternalWorklist() || "",
    tableName: SingleOrderDetailsTableName.AUTH_ON_FILE,
  });
  const { runInsert, abort: abortInsert } = useAuthStatusCommentsInsert({
    orderId: orderId ?? "",
    orgId: getOrgIdForFetchExternalWorklist() || "",
  });
  const { fetchAll, coverageDetails, filteredSiblingOrders } =
    useDataAccumulatorForSingleOrder();

  const { setHasCptTableAnyError } = useBusniessOfficeNotesContext();

  useEffect(() => {
    return () => {
      abort();
      abortInsert();
    };
  }, [abort, abortInsert]);

  /**
   * Discards unsaved CPT edits, restores the last-saved table snapshot, and
   * clears staged auth-status comments plus denial/query reasons for the order.
   */
  const handleProceedWithoutSaving = () => {
    setHasChanges(false);
    setTableData(originalTableData);
    saveTableDataToContext(originalTableData);
    removeAuthStatusChangeComments(orderId ?? "");
    clearDenialQueryReasonsFromStoreByOrderId(orderId ?? "");
    setHasCptTableAnyError(false);
    blockerProceed?.();
  };

  /**
   * Persists the selected hold-until date on the auth-on-file record.
   *
   * @param selectedDate - Hold expiration date chosen in the hold modal.
   * @returns A promise that resolves after the update attempt completes.
   */
  const handleSaveHold = async (selectedDate: Date) => {
    setIsHoldSaving(true);
    const result = await runUpdate({
      date_of_hold_until: moment(selectedDate).toISOString(),
    });
    setIsHoldSaving(false);
    if (result?.success && result.data?.success) {
      dispatch(closeModal());
      toast.success(ToastMessages.HOLD_SAVED_SUCCESS);
    } else {
      toast.error(ToastMessages.HOLD_SAVE_ERROR);
    }
  };

  /**
   * Persists CPT table edits after the user confirms in the dialog.
   *
   * @param selectedOrderIds - Optional linked sibling order ids chosen in the
   *   confirmation dialog; when present, the same CPT payload is posted for
   *   the parent order and each eligible sibling in parallel. Mapped parent
   *   statuses skip same/earlier-DOS siblings and rewrite auth status on
   *   later-DOS siblings via `CPT_SIBLING_STATUS_MAPPINGS`.
   */
  const handleSaveChanges = async (selectedOrderIds?: string[]) => {
    if (!hasChanges) {
      toast.error(ToastMessages.NO_CHANGES_TO_SAVE);
      return;
    }
    try {
      apiWorkerManager.enqueueAndProcess({
        patient_id:
          generalOrderDetails?.demographics?.patient_id ??
          generalOrderDetails?.demographics?.patient_mrn ??
          "",
        order_id: generalOrderDetails?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.UPDATE_J_CODES_AUTH_STATUS_INITIATED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          payer_name: payerName,
        },
      });
      setIsSaving(true);
      await insertAuthStatusCommentsFromStore({
        generalOrderDetails,
        coverages: coverageDetails?.coverages ?? [],
        runInsert,
      });
      await flushDenialQueryReasonsToApi({
        orderId: orderId ?? "",
        regimenName: generalOrderDetails?.payload?.regimen_name ?? "",
      });
      const cptPayload = transformTableDataToApiPayload(
        tableData,
        authStatusOptions ?? [],
        orderId ?? "",
      );
      if (cptPayload) {
        const parentOrderId = orderId ?? "";

        await updateCptTablesForOrderIds(
          cptPayload,
          parentOrderId,
          selectedOrderIds,
          {
            parentAuthOnFile: authOnFileDetails,
            parentDateOfService:
              generalOrderDetails?.payload?.date_of_service ?? undefined,
            siblings: filteredSiblingOrders?.siblings,
          },
        );

        await fetchAll(parentOrderId, getOrgIdForFetchExternalWorklist() || "");
      }
      setOriginalTableData([...tableData]);
      setHasChanges(false);
      removeBoComments(orderId ?? "");
      removeCptCodes(orderId ?? "");
      removeAuthStatusChangeComments(orderId ?? "");
      clearDenialQueryReasonsFromStoreByOrderId(orderId ?? "");
      dispatch(closeModal());
      toast.success(ToastMessages.CPT_CODES_UPDATED_SUCCESS);
    } catch (error) {
      showApiError(error, ToastMessages.CPT_CODES_UPDATED_FAILURE);
    } finally {
      setIsSaving(false);
      blockerProceed?.();
    }
  };

  return {
    isSaving,
    setIsSaving,
    isHoldSaving,
    handleSaveChanges,
    handleSaveHold,
    handleProceedWithoutSaving,
  };
};
